import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Dialog, Divider, InputBase, Typography } from '@mui/material';
import { ArrowDown, ArrowUp, CornerDownLeft, EyeOff, LogOut, Plus, Search, UserPlus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Kbd } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { openModal, selectModal } from '../../store/slices/uiSlice';
import { AuthRole } from '../../utils/roles';
import { sbg, typeStyle } from '../../theme/sbgTokens';
import { getNavigationSections } from './Sidebar';
import { THEME_MODES, useThemeMode } from './ThemeModeMenu';

/*
 * Command palette (book §11.7, §12.10): ⌘K / Ctrl+K from anywhere. Pages are
 * the role's sidebar destinations; Actions are only things the app already
 * does (opening an existing form, switching theme, signing out), so nothing
 * here grants access the sidebar and routes don't.
 */

type GroupName = 'Pages' | 'Actions';

interface Command {
  id: string;
  group: GroupName;
  label: string;
  /** Right-aligned context: the page's section, "Current page", "On". Not searched. */
  hint?: string;
  icon: LucideIcon;
  keywords?: readonly string[];
  run: () => void;
}

const GROUPS: GroupName[] = ['Pages', 'Actions'];
const MANAGES_PEOPLE: readonly AuthRole[] = ['admin', 'hr'];

/** Fuzzy-ish match: prefix beats word start beats substring beats in-order letters. */
const scoreText = (text: string, query: string) => {
  const value = text.toLowerCase();
  if (value.startsWith(query)) return 4;
  if (value.split(/[\s/-]+/).some((word) => word.startsWith(query))) return 3;
  if (value.includes(query)) return 2;
  let matched = 0;
  for (const char of value) {
    if (char === query[matched]) matched += 1;
    if (matched === query.length) return 1;
  }
  return 0;
};

const scoreCommand = (command: Command, query: string) => {
  if (!query) return 1;
  const label = scoreText(command.label, query);
  const extra = Math.max(0, ...(command.keywords ?? []).map((word) => scoreText(word, query)));
  // The label wins ties so "Time off" ranks above a page that only lists "time" as a keyword.
  return Math.max(label ? label + 0.5 : 0, extra > 1 ? extra : 0);
};

export const isMacPlatform = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);

/**
 * ⌘K on Mac, Ctrl+K elsewhere. Matches the physical K key so non-Latin
 * layouts work, and leaves Ctrl+K (delete to end of line) alone on macOS.
 */
const isPaletteShortcut = (event: KeyboardEvent, mac: boolean) => {
  const isK = event.code ? event.code === 'KeyK' : typeof event.key === 'string' && event.key.toLowerCase() === 'k';
  if (!isK || event.altKey || event.shiftKey) return false;
  return mac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
};

/** Another dialog, drawer or menu is open (a closed keep-mounted one carries MuiModal-hidden). */
const anotherLayerIsOpen = () =>
  typeof document !== 'undefined' && document.querySelector('.MuiModal-root:not(.MuiModal-hidden)') !== null;

/** The ⌘K / Ctrl K hint shown on the top bar trigger. */
export const PaletteShortcut: React.FC = () => (
  <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', gap: 0.5 }}>
    <Kbd>{isMacPlatform() ? '⌘' : 'Ctrl'}</Kbd>
    <Kbd>K</Kbd>
  </Box>
);

interface PaletteBodyProps {
  onClose: () => void;
  /** Runs after the palette has finished closing (used to open a form dialog). */
  afterClose: (task: () => void) => void;
}

const PaletteBody: React.FC<PaletteBodyProps> = ({ onClose, afterClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { profile, actualRole, viewAsRole, setViewAsRole, signOut } = useAuth();
  const { mode, setMode } = useThemeMode();
  const role = (profile?.role || 'employee') as AuthRole;
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const baseId = useId();
  const listboxId = `${baseId}-results`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const commands = useMemo<Command[]>(() => {
    const { sections, tools } = getNavigationSections(role);
    const pages: Command[] = [...sections, tools].flatMap((section) =>
      section.items.map((item) => {
        const current = Boolean(matchPath({ path: item.path, end: item.exact ?? false }, location.pathname));
        return {
          id: `page:${item.path}`,
          group: 'Pages' as const,
          label: item.text,
          hint: current ? 'Current page' : section.title,
          icon: item.icon,
          keywords: [...(item.keywords ?? []), section.title],
          run: () => navigate(item.path),
        };
      }),
    );

    const actions: Command[] = [];
    if (MANAGES_PEOPLE.includes(role)) {
      // The same flows as the "Add employee" / "Add department" buttons on those pages.
      actions.push(
        {
          id: 'action:add-employee',
          group: 'Actions',
          label: 'Add employee',
          hint: 'Employees',
          icon: UserPlus,
          keywords: ['new employee', 'hire', 'create'],
          run: () => {
            navigate('/employees');
            afterClose(() => dispatch(openModal({ type: 'employee-form', data: { isEdit: false } })));
          },
        },
        {
          id: 'action:add-department',
          group: 'Actions',
          label: 'Add department',
          hint: 'Departments',
          icon: Plus,
          keywords: ['new department', 'team', 'create'],
          run: () => {
            navigate('/departments');
            afterClose(() => dispatch(openModal({ type: 'department-form', data: { isEdit: false } })));
          },
        },
      );
    }
    THEME_MODES.forEach(({ value, label, icon }) => {
      actions.push({
        id: `action:theme-${value}`,
        group: 'Actions',
        label: value === 'system' ? 'Match system theme' : `Use ${label.toLowerCase()} theme`,
        hint: mode === value ? 'On' : undefined,
        icon,
        keywords: ['theme', 'appearance', 'mode', 'color', label],
        run: () => setMode(value),
      });
    });
    if (viewAsRole && actualRole === 'admin') {
      actions.push({
        id: 'action:exit-preview',
        group: 'Actions',
        label: 'Exit role preview',
        icon: EyeOff,
        keywords: ['preview', 'view as', 'admin'],
        run: () => setViewAsRole(null),
      });
    }
    actions.push({
      id: 'action:sign-out',
      group: 'Actions',
      label: 'Sign out',
      icon: LogOut,
      keywords: ['log out', 'logout'],
      run: () => {
        signOut();
      },
    });
    return [...pages, ...actions];
  }, [role, location.pathname, navigate, afterClose, dispatch, mode, setMode, viewAsRole, actualRole, setViewAsRole, signOut]);

  const normalized = query.trim().toLowerCase();
  const groups = useMemo(
    () =>
      GROUPS.map((name) => ({
        name,
        items: commands
          .filter((command) => command.group === name)
          .map((command, order) => ({ command, order, score: scoreCommand(command, normalized) }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score || a.order - b.order)
          .map(({ command }) => command),
      })).filter((group) => group.items.length > 0),
    [commands, normalized],
  );
  const visible = useMemo(() => groups.flatMap((group) => group.items), [groups]);
  const active = Math.min(activeIndex, Math.max(visible.length - 1, 0));

  useEffect(() => {
    setActiveIndex(0);
  }, [normalized]);

  useEffect(() => {
    document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' });
    // optionId is derived from a stable id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const runCommand = (command: Command | undefined) => {
    if (!command) return;
    onClose();
    command.run();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (visible.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((active + step + visible.length) % visible.length);
    } else if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      runCommand(visible[active]);
    }
  };

  const pageExamples = commands.filter((command) => command.group === 'Pages').slice(0, 2).map((command) => command.label);
  let flatIndex = -1;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }} onKeyDown={handleKeyDown}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, height: 56, flex: 'none', color: sbg.text.secondary }}>
        <Search size={20} aria-hidden="true" />
        <InputBase
          autoFocus
          fullWidth
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search pages and actions…"
          inputProps={{
            role: 'combobox',
            'aria-label': 'Search pages and actions',
            'aria-expanded': visible.length > 0,
            'aria-controls': listboxId,
            'aria-autocomplete': 'list',
            'aria-activedescendant': visible.length > 0 ? optionId(active) : undefined,
            autoComplete: 'off',
            spellCheck: false,
          }}
          sx={{
            height: 48,
            ...typeStyle('body-md'),
            color: sbg.text.primary,
            '& input': { height: 24, padding: 0 },
          }}
        />
        <Box sx={{ display: { xs: 'none', sm: 'inline-flex' } }} aria-hidden="true">
          <Kbd>Esc</Kbd>
        </Box>
      </Box>
      <Divider />

      <Box
        id={listboxId}
        role="listbox"
        aria-label="Pages and actions"
        hidden={visible.length === 0}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1, display: visible.length === 0 ? 'none' : 'grid', alignContent: 'start', gap: 1 }}
      >
        {groups.map((group) => (
          <Box key={group.name} role="group" aria-labelledby={`${baseId}-${group.name}`}>
            <Typography
              id={`${baseId}-${group.name}`}
              variant="overline"
              component="div"
              sx={{ px: 1.5, pt: 1, pb: 0.5, color: sbg.text.tertiary }}
            >
              {group.name}
            </Typography>
            {group.items.map((command) => {
              flatIndex += 1;
              const index = flatIndex;
              const selected = index === active;
              const Icon = command.icon;
              return (
                <Box
                  key={command.id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={selected}
                  onMouseMove={() => {
                    if (!selected) setActiveIndex(index);
                  }}
                  onClick={() => runCommand(command)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    height: 40,
                    px: 1.5,
                    borderRadius: sbg.radius.control,
                    cursor: 'pointer',
                    color: sbg.text.primary,
                    bgcolor: selected ? sbg.bg.selected : 'transparent',
                    '& .sbg-command-icon': { flex: 'none', color: selected ? sbg.text.accent : sbg.text.secondary },
                  }}
                >
                  <Icon className="sbg-command-icon" size={18} aria-hidden="true" />
                  <Typography variant="labelMd" component="span" noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {command.label}
                  </Typography>
                  {command.hint && (
                    <Typography variant="body2" component="span" noWrap sx={{ color: sbg.text.secondary, flex: 'none' }}>
                      {command.hint}
                    </Typography>
                  )}
                  <Box
                    aria-hidden="true"
                    sx={{ flex: 'none', width: 28, display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-end', visibility: selected ? 'visible' : 'hidden' }}
                  >
                    <Kbd>
                      <CornerDownLeft size={12} />
                    </Kbd>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Outside the listbox: a listbox may only own options and groups. */}
      {visible.length === 0 && (
        <Box sx={{ flex: 1, py: 5, px: 2, textAlign: 'center', display: 'grid', gap: 0.5, justifyItems: 'center', alignContent: 'start' }}>
          <Typography variant="labelMd" component="p" sx={{ color: sbg.text.primary }}>
            No pages or actions match “{query.trim()}”
          </Typography>
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, maxWidth: '40ch' }}>
            {pageExamples.length > 1
              ? `Try a page name, like ${pageExamples[0]} or ${pageExamples[1]}.`
              : 'Try a page name or an action, like Sign out.'}
          </Typography>
        </Box>
      )}

      <Box className="sbg-sr-only" aria-live="polite">
        {normalized ? `${visible.length} ${visible.length === 1 ? 'result' : 'results'}` : ''}
      </Box>

      <Box
        aria-hidden="true"
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          gap: 2,
          height: 40,
          flex: 'none',
          px: 2.5,
          borderTop: `1px solid ${sbg.border.subtle}`,
          bgcolor: sbg.bg.surfaceSubtle,
          color: sbg.text.secondary,
          ...typeStyle('caption'),
        }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Kbd>
            <ArrowUp size={12} />
          </Kbd>
          <Kbd>
            <ArrowDown size={12} />
          </Kbd>
          Move
        </Box>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Kbd>
            <CornerDownLeft size={12} />
          </Kbd>
          Open
        </Box>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Kbd>Esc</Kbd>
          Close
        </Box>
      </Box>
    </Box>
  );
};

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onOpenChange }) => {
  const { isOpen: modalOpen } = useSelector(selectModal);
  const latest = useRef({ open, onOpenChange, modalOpen });
  latest.current = { open, onOpenChange, modalOpen };
  const pendingTask = useRef<(() => void) | null>(null);

  // ⌘K (Mac) / Ctrl+K toggles the palette from anywhere in the app — except
  // over another open dialog or form: jumping away from it, or replacing the
  // single Redux modal slot, would throw away what someone has typed.
  useEffect(() => {
    const mac = isMacPlatform();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isPaletteShortcut(event, mac)) return;
      const current = latest.current;
      if (!current.open && (current.modalOpen || anotherLayerIsOpen())) return;
      event.preventDefault();
      // Holding the keys auto-repeats; only the first press toggles.
      if (event.repeat) return;
      current.onOpenChange(!current.open);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const close = React.useCallback(() => onOpenChange(false), [onOpenChange]);
  const afterClose = React.useCallback((task: () => void) => {
    pendingTask.current = task;
  }, []);

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={false}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      slotProps={{
        paper: {
          'aria-label': 'Search or jump to',
          sx: {
            width: 640,
            maxWidth: 'calc(100% - 32px)',
            maxHeight: { xs: 'calc(100% - 12vh - 16px)', sm: 'min(560px, calc(100% - 20vh - 24px))' },
            m: 0,
            mt: { xs: '12vh', sm: '20vh' },
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        },
        transition: {
          onExited: () => {
            const task = pendingTask.current;
            pendingTask.current = null;
            // The app has one modal slot; never replace a form that opened meanwhile.
            if (task && !latest.current.modalOpen) task();
          },
        },
      }}
    >
      <PaletteBody onClose={close} afterClose={afterClose} />
    </Dialog>
  );
};

export default CommandPalette;
